import React, { useState } from "react";
import RoomTypeCard from "./roomType/roomType";
import RoomCategoryCard from "./roomCategory/roomCategory";
import RoomClassCard from "./roomClass/roomClass";
import RateClassCard from "./rateClass/rateClass";
import RateCategoryCard from "./rateCategory/rateCategory";
import RateCard from "./rate/rate";
import RatePriceCard from "./ratePrice/ratePrice";
import UsersCard from "./propertyUser/propertyUsers";
import AmenityCategoryCard from "./amenityCategory/amenityCategory";
import {
  HomeIcon,
  ViewBoardsIcon,
  SparklesIcon,
  ArrowLeftIcon,
  CogIcon,
  UserGroupIcon,
  CashIcon,
  TagIcon,
  CollectionIcon,
  OfficeBuildingIcon,
  LightningBoltIcon,
} from "@heroicons/react/outline";

const Configurations = () => {
  const [selectedCard, setSelectedCard] = useState(null);

  const configurations = [
    {
      id: "Card Nine",
      title: "Amenity Categories",
      description: "Manage property amenities and features",
      icon: SparklesIcon,
      color: "from-yellow-400 to-orange-500",
      bgColor: "from-yellow-50 to-orange-50",
    },
    {
      id: "Card Eight",
      title: "Property Users",
      description: "Configure property staff and permissions",
      icon: UserGroupIcon,
      color: "from-blue-400 to-indigo-500",
      bgColor: "from-blue-50 to-indigo-50",
    },
    {
      id: "Card Seven",
      title: "Rate Pricing",
      description: "Set up pricing strategies and rates",
      icon: CashIcon,
      color: "from-green-400 to-emerald-500",
      bgColor: "from-green-50 to-emerald-50",
    },
    {
      id: "Card Six",
      title: "Rate Plans",
      description: "Create and manage rate plans",
      icon: TagIcon,
      color: "from-purple-400 to-pink-500",
      bgColor: "from-purple-50 to-pink-50",
    },
    {
      id: "Card Five",
      title: "Rate Categories",
      description: "Organize rates into categories",
      icon: CollectionIcon,
      color: "from-pink-400 to-rose-500",
      bgColor: "from-pink-50 to-rose-50",
    },
    {
      id: "Card Four",
      title: "Rate Classes",
      description: "Define rate class structures",
      icon: LightningBoltIcon,
      color: "from-cyan-400 to-blue-500",
      bgColor: "from-cyan-50 to-blue-50",
    },
    {
      id: "Card Three",
      title: "Room Classes",
      description: "Configure room class hierarchies",
      icon: OfficeBuildingIcon,
      color: "from-indigo-400 to-purple-500",
      bgColor: "from-indigo-50 to-purple-50",
    },
    {
      id: "Card Two",
      title: "Room Categories",
      description: "Set up room category groupings",
      icon: ViewBoardsIcon,
      color: "from-teal-400 to-cyan-500",
      bgColor: "from-teal-50 to-cyan-50",
    },
    {
      id: "Card One",
      title: "Room Types",
      description: "Manage different room type configurations",
      icon: HomeIcon,
      color: "from-violet-400 to-purple-500",
      bgColor: "from-violet-50 to-purple-50",
    },
  ];

  const renderCardContent = () => {
    switch (selectedCard) {
      case "Card One":
        return <RoomTypeCard />;
      case "Card Two":
        return <RoomCategoryCard />;
      case "Card Three":
        return <RoomClassCard />;
      case "Card Four":
        return <RateClassCard />;
      case "Card Five":
        return <RateCategoryCard />;
      case "Card Six":
        return <RateCard />;
      case "Card Seven":
        return <RatePriceCard />;
      case "Card Eight":
        return <UsersCard />;
      case "Card Nine":
        return <AmenityCategoryCard />;
      default:
        return null;
    }
  };

  const selectedConfig = configurations.find((c) => c.id === selectedCard);

  return (
    <div className="configurations-container">
      {selectedCard ? (
        <div className="config-detail-view">
          {/* Back Navigation */}
          <div className="back-nav">
            <button
              onClick={() => setSelectedCard(null)}
              className="config-back-button"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Back to Configurations</span>
            </button>
          </div>

          {/* Selected Config Header */}
          {selectedConfig && (
            <div className="config-header">
              <div
                className={`config-header-gradient bg-gradient-to-br ${selectedConfig.bgColor}`}
              >
                <div className="config-header-content">
                  <div
                    className={`config-icon-large bg-gradient-to-br ${selectedConfig.color}`}
                  >
                    {React.createElement(selectedConfig.icon, {
                      className: "w-8 h-8",
                    })}
                  </div>
                  <div>
                    <h2 className="config-title">{selectedConfig.title}</h2>
                    <p className="config-description">
                      {selectedConfig.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="config-content">{renderCardContent()}</div>
        </div>
      ) : (
        <div className="configs-grid-view">
          {/* Header */}
          <div className="configs-header">
            <div>
              <h2 className="section-title">Property Configurations</h2>
              <p className="section-subtitle">
                Manage all aspects of your property settings
              </p>
            </div>
            <div className="stats-badge">
              <CogIcon className="w-5 h-5" />
              <span>{configurations.length} Configuration Options</span>
            </div>
          </div>

          {/* Configuration Cards Grid */}
          <div className="configs-grid">
            {configurations.map((config, index) => {
              const Icon = config.icon;
              return (
                <div
                  key={config.id}
                  onClick={() => setSelectedCard(config.id)}
                  className="config-card"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div
                    className={`config-card-gradient bg-gradient-to-br ${config.bgColor}`}
                  >
                    <div className="config-card-content">
                      <div
                        className={`config-icon bg-gradient-to-br ${config.color}`}
                      >
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="config-info">
                        <h3 className="config-card-title">{config.title}</h3>
                        <p className="config-card-description">
                          {config.description}
                        </p>
                      </div>
                      <div className="config-arrow">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Styles */}
      <style jsx>{`
        .configurations-container {
          padding: 0;
        }

        .configs-grid-view {
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .configs-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.25rem 0;
        }

        .section-subtitle {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0;
        }

        .stats-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          background: linear-gradient(135deg, #f3f4f6, #ffffff);
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .configs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.5rem;
        }

        .config-card {
          cursor: pointer;
          animation: slideUp 0.4s ease forwards;
          opacity: 0;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .config-card-gradient {
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          padding: 1.5rem;
          transition: all 0.3s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          position: relative;
          overflow: hidden;
        }

        .config-card-gradient::before {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 150px;
          height: 150px;
          background: radial-gradient(
            circle,
            rgba(255, 255, 255, 0.4) 0%,
            transparent 70%
          );
          border-radius: 50%;
          transform: translate(30%, -30%);
          transition: transform 0.3s ease;
        }

        .config-card:hover .config-card-gradient {
          transform: translateY(-8px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15);
          border-color: transparent;
        }

        .config-card:hover .config-card-gradient::before {
          transform: translate(30%, -30%) scale(1.2);
        }

        .config-card-content {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .config-icon {
          width: 4rem;
          height: 4rem;
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
          transition: transform 0.3s ease;
        }

        .config-card:hover .config-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .config-info {
          flex: 1;
        }

        .config-card-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
          transition: color 0.3s ease;
        }

        .config-card:hover .config-card-title {
          color: #8b5cf6;
        }

        .config-card-description {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0;
          line-height: 1.5;
        }

        .config-arrow {
          align-self: flex-end;
          width: 2.5rem;
          height: 2.5rem;
          background: white;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #8b5cf6;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .config-card:hover .config-arrow {
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          transform: translateX(4px);
        }

        .config-detail-view {
          animation: fadeIn 0.3s ease;
        }

        .back-nav {
          margin-bottom: 1.5rem;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1.25rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-weight: 600;
          font-size: 0.875rem;
          color: #374151;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .back-button:hover {
          background: #f9fafb;
          border-color: #8b5cf6;
          color: #8b5cf6;
          transform: translateX(-4px);
        }

        .config-back-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1.25rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-weight: 600;
          font-size: 0.875rem;
          color: #374151;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .config-back-button:hover {
          background: #f9fafb;
          border-color: #8b5cf6;
          color: #8b5cf6;
          transform: translateX(-4px);
        }

        .config-header {
          margin-bottom: 2rem;
        }

        .config-header-gradient {
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          padding: 2rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          position: relative;
          overflow: hidden;
        }

        .config-header-gradient::before {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 300px;
          height: 300px;
          background: radial-gradient(
            circle,
            rgba(255, 255, 255, 0.4) 0%,
            transparent 70%
          );
          border-radius: 50%;
          transform: translate(30%, -30%);
        }

        .config-header-content {
          position: relative;
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .config-icon-large {
          width: 5rem;
          height: 5rem;
          border-radius: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
          flex-shrink: 0;
        }

        .config-title {
          font-size: 1.875rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .config-description {
          font-size: 1rem;
          color: #6b7280;
          margin: 0;
        }

        .config-content {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          padding: 2rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        @media (max-width: 1024px) {
          .configs-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .configs-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          }

          .config-header-content {
            flex-direction: column;
            align-items: flex-start;
            text-align: left;
          }
        }

        @media (max-width: 640px) {
          .configs-grid {
            grid-template-columns: 1fr;
          }

          .section-title {
            font-size: 1.25rem;
          }

          .config-title {
            font-size: 1.5rem;
          }

          .config-content {
            padding: 1rem;
          }

          .stats-badge {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Configurations;
